import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { tap } from 'rxjs/operators';
import { ApiDataSource } from '../../../utils/api-data-source';
import { MatSort, Sort } from '@angular/material/sort';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { UserModel } from '../../../models/user.model';
import { MatPaginator } from '@angular/material/paginator';
import { RbacRole } from '../../../interfaces/rbac-role.interface';
import { RbacPermission } from '../../../interfaces/rbac-permission.interface';
import { RbacRolesTabsComponent } from '../rbac-roles-tabs/rbac-roles-tabs.component';
import { RBACService } from '../../../shared/rbac.service';
import { Router } from '@angular/router';
import { RbacRolesFilterComponent } from '../rbac-roles-filter/rbac-roles-filter.component';
import { RbacRolesFilters } from '../rbac-roles-filter/rbac-roles-filters.interface';

@Component({
  selector: 'app-rbac-roles',
  templateUrl: './rbac-roles.component.html',
  styleUrls: ['./rbac-roles.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RbacRolesComponent implements OnInit {

  // The columns that should show in the list.
  listColumns: string[] = [];

  // The list data source.
  listDataSource: RbacRolesDataSource = new RbacRolesDataSource(this.app, this.api);

  // RBAC permission flags. These are resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // The paginator and sorter.
  @ViewChild('paginator1', {static: false}) paginator1: MatPaginator;
  @ViewChild('paginator2', {static: false}) paginator2: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Resolve the RBAC permission flags first so they are always set, even when we return early.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_DELETE)
    ], true);

    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_VIEW)
    ], true);

    // Page-level access: redirect only when the user holds no RBAC roles permission at all.
    // A create-only user may still open the page to add roles.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to access RBAC roles.');
      this.router.navigate(['/dashboard']);
      return;
    }

    // Build listColumns dynamically based on permissions
    this.listColumns = ['id', 'name', 'rbac_permissions_count', 'users_count', 'date_created', 'date_modified'];

    // Add select column if user has Delete permission
    if (this.canDelete) {
      this.listColumns.unshift('select');
    }

    // Add actions column if user has Edit or Delete permission
    if (this.canEdit || this.canDelete) {
      this.listColumns.push('actions');
    }

    // Only load the list when the user can view it.
    if (this.canView) {
      // Match the initial sort shown in the table header (matSortActive="date_created", desc).
      this.listDataSource.order_by = 'date_created';
      this.listDataSource.order = 'desc';
      this.listDataSource.getData(true);
    }
  }

  ngAfterViewInit(): void {
    // The paginators and sorter only exist when the list is shown.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting takes place.
    this.sort.sortChange.subscribe((): void => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    // Define the paginator tap events.
    const paginatorTap = tap((paginator): void => {
      // Set the page index and size for both paginator(s).
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      // Set the limit and offset in the dara source.
      this.listDataSource.limit = paginator['pageSize'];
      this.listDataSource.offset = paginator['pageIndex'];

      // Refresh the list data but do not reset the offset.
      this.listDataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter: Sort): void => {
      // Sort by the selected column, not just the direction.
      this.listDataSource.order_by = sorter['active'];
      this.listDataSource.order = sorter['direction'];
      this.listDataSource.getData(true);
    })).subscribe();
  }

  /**
   * Create a new or edit an existing rbac role.
   *
   * @param rbac_role_id The id of the rbac role.
   */
  onEditRbacRole(rbac_role_id?: number): void {
    // Check create or edit permission based on whether we're creating or editing
    if (!rbac_role_id) {
      // Create flow - requires View + Create
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_CREATE)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to create RBAC roles.');
        return;
      }
    } else {
      // Edit flow - requires View + Edit
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_EDIT)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit RBAC roles.');
        return;
      }
      // Partner roles are read-only to clients (they may only view them).
      const loadedRole: RbacRole = (this.listDataSource.dataSubject.value || []).find((rbac_role: RbacRole): boolean => {
        return rbac_role.id === rbac_role_id;
      });
      if (this.isLockedRole(loadedRole) || this.lockedRoleIds().includes(rbac_role_id)) {
        this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
        return;
      }
    }

    this.openRbacRoleDialog(rbac_role_id);
  }

  /**
   * Open an existing rbac role read-only. Used for partner roles, which clients may only view.
   *
   * @param rbac_role_id The id of the rbac role.
   */
  onViewRbacRole(rbac_role_id: number): void {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view RBAC roles.');
      return;
    }
    this.openRbacRoleDialog(rbac_role_id);
  }

  /**
   * Open the rbac role dialog and refresh the list when it closes.
   *
   * @param rbac_role_id The id of the rbac role, or nothing to create one.
   */
  private openRbacRoleDialog(rbac_role_id?: number): void {
    this.utils.showComponentDialog(RbacRolesTabsComponent, {
      rbac_role_id: rbac_role_id
    }, {
      width: '800px'
    })
      .then((): void => {
        // Refresh the list after the dialog closed (only when the user can view it).
        if (this.canView) {
          this.listDataSource.getData();
        }
      });
  }

  /**
   * Remove the rbac role.
   * @param {number} rbac_role_id - The id of the rbac role to delete.
   * @param {any} evt - The element reference to place the confirmation dialog in the right place.
   * @return {void}
   */
  onDeleteRbacRole(rbac_role_id: number, evt: any): void {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete RBAC roles.');
      return;
    }

    // Partner roles can only be removed by support.
    if (this.lockedRoleIds().includes(rbac_role_id)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    this.utils.showQuickActions(evt.target, 'Are you sure you want to delete the RBAC role?', [
      {
        text: 'Yes',
        handler: (): void => {
          this.api.makeRequest('delete', `v2/account/rbac-roles/${rbac_role_id}`)
            .then((): void => {
              // Refresh the list after the removal of the rbac role.
              this.listDataSource.getData();
              // Show a success toast message.
              this.utils.showToast('The RBAC role was deleted.');
            })
            .catch((error: any): void => {
              // Show the error response.
              this.utils.handleAPIErrors(error);
            });
        }
      },
      {
        text: 'No',
        handler: (): void => { }
      }
    ]);
  }

  /**
   * Deletes selected RBAC roles.
   *
   * @returns {void}
   */
  onDeleteSelectedRbacRoles(): void {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete RBAC roles.');
      return;
    }

    // Get the selected rbac role ids.
    const ids: any[] = this.listDataSource.selection.selected;
    // Check if we have rbac roles selected.
    if ( ids.length == 0 ) {
      this.utils.showModal('Delete RBAC Roles', 'Please select some RBAC roles to delete.');
      return;
    }

    // Partner roles can only be removed by support.
    const lockedIds: number[] = this.lockedRoleIds();
    if ( ids.some((id: number): boolean => lockedIds.includes(id)) ) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }
    // Make a request to delete the rbac roles.
    this.utils.showModal('Delete RBAC Roles', `Are you sure you want to delete (${ids.length}) RBAC role/s?`, (): void => {
      this.api.makeRequest('delete', `v2/account/rbac-roles/${ids.join(',')}`)
        .then((): void => {
          // Refresh the list after the rbac roles was deleted and deselect all records.
          this.listDataSource.deselectAll();
          this.listDataSource.getData();
          // Show a success message.
          this.utils.showToast('The selected RBAC roles was deleted.');
        })
        .catch((error): void => {
          // Show an error response
          this.utils.handleAPIErrors(error);
        });
    });
  }

  /**
   * Retrieves the contact persons of the users associated with a given rbac role.
   *
   * @returns A string containing the contact persons of the users associated with the rbac role, separated by new lines.
   * @param rbac_role
   */
  getRoleUsersForTooltip(rbac_role: RbacRole): string {
    return rbac_role.users.map((user: UserModel): string => {
      return user.contact_person;
    }).join('\n');
  }

  /**
   * Whether the given role is a system-managed default role (Administrators / Users). Default roles
   * cannot be renamed, have their permissions changed, or be deleted; only their employees change.
   * The is_default flag is stored as 0/1, so it is coerced to a boolean here.
   *
   * @param rbac_role The role to check.
   * @returns True when the role is a default role.
   */
  isDefaultRole(rbac_role: RbacRole): boolean {
    return !!rbac_role?.is_default;
  }

  /**
   * Whether the given role is a partner role (managed by the partner sync).
   *
   * @param rbac_role The role to check.
   * @returns True when the role is a partner role.
   */
  isPartnerRole(rbac_role: RbacRole): boolean {
    return rbac_role?.partner_user_id != null;
  }

  /**
   * Whether the given role is a partner role that is read-only to the current user. Always false for
   * super admins.
   *
   * @param rbac_role The role to check.
   * @returns True when the role is locked.
   */
  isLockedRole(rbac_role: RbacRole): boolean {
    return this.rbac.partnerLockApplies(this.isPartnerRole(rbac_role));
  }

  /**
   * Every partner role id of the account (from the list response) when the lock applies, else none.
   *
   * @returns {number[]}
   */
  lockedRoleIds(): number[] {
    if ( !this.rbac.partnerLockApplies(true) ) {
      return [];
    }
    return this.listDataSource.lastResponse?.partnerRoleIds ?? [];
  }

  /**
   * The roles on the current page the user may select. Default roles cannot be deleted, so they have
   * no row checkbox and must never end up in the selection. Locked partner roles are excluded too.
   *
   * @returns {RbacRole[]}
   */
  private getSelectableRoles(): RbacRole[] {
    return (this.listDataSource.dataSubject.value || []).filter((rbac_role: RbacRole): boolean => {
      return !this.isDefaultRole(rbac_role) && !this.isLockedRole(rbac_role);
    });
  }

  /**
   * Whether the current page has at least one selectable role. An account that only has the two
   * default roles has none, so the header checkbox is hidden entirely: there would be nothing for it
   * to select.
   *
   * @returns {boolean}
   */
  hasSelectableRoles(): boolean {
    return this.getSelectableRoles().length > 0;
  }

  /**
   * Whether every selectable role on the current page is selected. Default roles are ignored, so the
   * "select all" state can actually be reached on a page that also lists them.
   *
   * @returns {boolean}
   */
  isAllSelectableSelected(): boolean {
    const selectable: RbacRole[] = this.getSelectableRoles();
    return selectable.length > 0 && selectable.every((rbac_role: RbacRole): boolean => {
      return this.listDataSource.selection.isSelected(rbac_role.id);
    });
  }

  /**
   * Selects or deselects every selectable role on the current page.
   *
   * This replaces the data source's selectAllToggle(), which selects every loaded row blindly and
   * would therefore put undeletable default roles into the selection, inflating the count shown by
   * the delete confirmation.
   *
   * @returns {void}
   */
  onToggleSelectAll(): void {
    const selectable: RbacRole[] = this.getSelectableRoles();
    if ( selectable.length === 0 ) { return; }

    const selectAll: boolean = !this.isAllSelectableSelected();
    selectable.forEach((rbac_role: RbacRole): void => {
      if ( selectAll ) {
        this.listDataSource.selection.select(rbac_role.id);
      } else {
        this.listDataSource.selection.deselect(rbac_role.id);
      }
    });
  }

  /**
   * Opens the filter dialog and applies the returned filters to the list.
   *
   * @returns {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(RbacRolesFilterComponent, {
      rbac_permission_ids: this.listDataSource.rbac_permission_ids,
      user_ids: this.listDataSource.user_ids
    }, {
      width: '700px'
    }, (results: RbacRolesFilters): void => {
      if ( typeof results === 'undefined' ) { return; }
      this.listDataSource.rbac_permission_ids = results.rbac_permission_ids || [];
      this.listDataSource.user_ids = results.user_ids || [];
      // Reset the offset. The filtered result set is smaller, so the current page may not exist.
      this.listDataSource.getData(true);
    });
  }

  /**
   * Whether any filter is currently applied. Drives the filter button's badge and the active
   * filter notice above the list.
   *
   * @returns {boolean}
   */
  isFiltered(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  /**
   * The total number of selected filter values across all filters. Shown as the badge on the filter
   * button so the user can see at a glance how much the list is narrowed.
   *
   * @returns {number}
   */
  getActiveFilterCount(): number {
    return this.listDataSource.rbac_permission_ids.length + this.listDataSource.user_ids.length;
  }

  /**
   * Clears all filters and reloads the list.
   *
   * @returns {void}
   */
  onClearFilters(): void {
    this.listDataSource.rbac_permission_ids = [];
    this.listDataSource.user_ids = [];
    this.listDataSource.getData(true);
  }

  protected readonly RBACService = RBACService;
}

/**
 * An API data source for retrieving RBAC roles data.
 */
export class RbacRolesDataSource extends ApiDataSource {

  // Set the default order and order by data.
  order_by: string = 'name';
  order: string = 'asc';

  // The active filters. Empty arrays mean the filter is not applied.
  rbac_permission_ids: number[] = [];
  user_ids: number[] = [];

  /**
   * Retrieves data from the API.
   *
   * @param {boolean} resetOffset - A flag indicating whether to reset the offset.
   * @returns {void}
   */
  getData(resetOffset: boolean = false): void {
    // Make a request to the API to get the data.
    this.makeRequest(`v2/account/rbac-roles`, resetOffset, {
      rbac_permission_ids: this.rbac_permission_ids.length ? this.rbac_permission_ids.join(',') : '',
      user_ids: this.user_ids.length ? this.user_ids.join(',') : ''
    });
  }
}

